/**
 * Shared shell for the patient drawer's data cards (Terminkontext,
 * Dauertermine).
 *
 * Each of those used to build its own Card three times over — once per
 * loading / error / loaded state — and the copies had drifted: the loading
 * and error variants carried `m: 1` and the default CardHeader typography,
 * while the loaded one had neither. So the card visibly changed width, offset
 * and header height at the instant the query resolved, on top of the jump
 * from a small spinner to full content. Every state rendering through this
 * one shell means only the body can change.
 */
import React from "react";
import { Box, Card, CardContent, CardHeader, CircularProgress } from "@mui/material";

interface DrawerCardProps {
  title: string;
  children: React.ReactNode;
  /** Trailing control in the header — an icon button, sized for a dense card. */
  action?: React.ReactNode;
}

export const DrawerCard: React.FC<DrawerCardProps> = ({ title, children, action }) => (
  <Card>
    <CardHeader
      title={title}
      titleTypographyProps={{ variant: "h6" }}
      action={action}
      // The action is an icon button, not a stack of buttons, so it centres on
      // the title rather than sitting proud of it.
      sx={{ pb: 0, pt: 1, "& .MuiCardHeader-action": { alignSelf: "center", margin: 0 } }}
    />
    <CardContent sx={{ pt: 0.5, pb: 1, "&:last-child": { pb: 1 } }}>{children}</CardContent>
  </Card>
);

/**
 * Centered spinner that fills the card's width and holds `minHeight` open, so
 * the placeholder approximates the height the loaded content will occupy
 * instead of collapsing the drawer and re-expanding it a moment later. Pass a
 * height only where the loaded size is predictable — reserving space that the
 * real content won't fill just trades a grow for a shrink.
 */
export const DrawerCardSpinner: React.FC<{ minHeight?: number }> = ({ minHeight }) => (
  <Box
    display="flex"
    justifyContent="center"
    alignItems="center"
    sx={{ width: "100%", minHeight, py: minHeight ? 0 : 3 }}
  >
    <CircularProgress />
  </Box>
);
